// Campaign lifecycle (spec 08 §5 controls). Mirrored by set_campaign_status / schedule_campaign (Task 21).
import { type EmailRuntimeConfig, isSendingAddress } from "./config.ts";

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "paused"
  | "sent"
  | "cancelled";
export type CampaignAction = "pause" | "resume" | "cancel";

const CANCELLABLE: readonly CampaignStatus[] = [
  "draft",
  "scheduled",
  "sending",
  "paused",
];

/** Next status for a control action, or null when the action is not allowed. */
export function nextCampaignStatus(
  status: CampaignStatus,
  action: CampaignAction,
  startedAt: string | null,
): CampaignStatus | null {
  if (action === "pause")
    return status === "scheduled" || status === "sending" ? "paused" : null;
  if (action === "resume")
    return status === "paused" ? (startedAt ? "sending" : "scheduled") : null;
  return CANCELLABLE.includes(status) ? "cancelled" : null;
}

export const isCampaignEditable = (status: CampaignStatus): boolean =>
  status === "draft";

export type ScheduleBlocker =
  | "campaign_not_draft"
  | "casl_ack_required"
  | "postal_address_missing"
  | "template_archived"
  | "template_draft"
  | "from_domain_invalid"
  | "daily_cap_invalid"
  | "daily_cap_too_high";

/** First reason scheduling is refused, in the same order as schedule_campaign; null when it may proceed. */
export function scheduleBlocker(
  c: {
    status: CampaignStatus;
    caslAcknowledged: boolean;
    postalAddress: string;
    templateArchived: boolean;
    /** Not yet approved by an admin (email_templates.draft). */
    templateDraft: boolean;
    fromEmail: string;
    dailyCap: number;
  },
  cfg: EmailRuntimeConfig,
): ScheduleBlocker | null {
  if (c.status !== "draft") return "campaign_not_draft";
  if (!c.caslAcknowledged) return "casl_ack_required";
  if (!c.postalAddress.trim()) return "postal_address_missing";
  if (c.templateArchived) return "template_archived";
  if (c.templateDraft) return "template_draft";
  if (!isSendingAddress(c.fromEmail, cfg)) return "from_domain_invalid";
  if (!Number.isInteger(c.dailyCap) || c.dailyCap < 1)
    return "daily_cap_invalid";
  if (c.dailyCap > cfg.dailyCap) return "daily_cap_too_high";
  return null;
}
